import { useEffect, useState } from "react";
import DentonHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Denton?", a: "We offer Denton-area clients expedited processing as fast as 24 hours. Standard expedited service typically takes 5–10 business days. Denton's proximity to DFW Airport — just 30 miles south — means Denton travelers often have tight booking-to-departure windows. The exact timeline depends on your application type and document completeness." },
  { q: "Where is the nearest passport acceptance facility in Denton?", a: "Denton has several acceptance facilities including the Denton Main Post Office, Flower Mound Post Office, and the Denton County Clerk's office. DFW Airport itself has a passport acceptance facility. We provide a complete list of facilities with appointment availability." },
  { q: "Can UNT students get expedited passports for study abroad?", a: "Absolutely. University of North Texas sends hundreds of students abroad annually. UNT's College of Music, College of Visual Arts & Design, and College of Business all have strong international programs. Program departure dates are fixed and non-negotiable. We process UNT student passports with priority review." },
  { q: "How does Denton's proximity to DFW Airport affect passport timing?", a: "Being 30 miles from DFW Airport is both an advantage and a pressure multiplier. Denton residents can be on an international flight within an hour of leaving home — but that accessibility also means people book last-minute international travel more readily, then discover passport issues with very little lead time." },
  { q: "How much does expedited passport service cost for Denton residents?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. Our rates are typically 30–100% lower than FedEx, Staples, and other resellers. We offer a best price guarantee." },
  { q: "Do I need an appointment at the Denton passport acceptance facility?", a: "Many Denton post offices require appointments for passport acceptance. The Denton County Clerk and some suburban post offices may offer walk-in service. We recommend calling ahead. We can help you identify the facility with the earliest appointment in the greater Denton area." },
  { q: "Can TWU students get expedited passports?", a: "Yes. Texas Woman's University students travel for study abroad programs, research presentations, and academic conferences. First-time applicants at TWU often apply the semester before travel — which is the right approach. If you are within 6 weeks of departure and haven't applied yet, contact us for expedited options." },
  { q: "What's the Denton music scene's connection to international travel?", a: "Denton has a legendary indie and jazz music scene centered on the Fry Street area, KNTU radio, and UNT's Jazz Studies program — one of the top in the world. Musicians, band managers, and tour organizers frequently travel internationally for tours, competitions, and recording sessions in Europe and Japan. These music industry trips often have immovable dates." },
  { q: "Can I get a passport for a quick trip to Mexico from Denton?", a: "Yes. Denton residents frequently fly to Cancun, Mexico City, or drive to the border. A passport book is required for international air travel. A passport card is cheaper and works for land crossings but not for flights. We process both books and cards with expedited service." },
  { q: "What if I'm moving to Denton from out of state and need a passport?", a: "New Denton residents who have never had a Texas address can still apply through any Denton-area acceptance facility — you don't need to have a Texas ID yet. We help navigate these transitional situations and ensure your documents meet State Department requirements." },
];

function DentonStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function DentonProcessingSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Denton Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Multiple speed tiers for UNT students, TWU students, DFW corridor travelers, and Denton families.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Denton residents with imminent DFW departure within 3 days. Same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For UNT students and Denton professionals with international travel in 1–2 weeks. Full document review included." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Denton residents planning ahead. Faster than standard post office processing with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Denton travelers with imminent DFW departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Denton residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time applications for UNT and TWU students, and Denton first-time international travelers." },
    { icon: "ri-graduation-cap-line", title: "Study Abroad", desc: "Priority service for UNT College of Music, Business, and Visual Arts international programs." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Flower Mound and Highland Village families." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 guidance for Denton residents." },
    { icon: "ri-music-line", title: "Music & Arts Travel", desc: "Expedited processing for UNT Jazz musicians and Denton arts community international travel." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before Denton applications reach the State Department." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Denton Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for UNT, TWU, Denton musicians, and North Texas families.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Denton</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Denton is one of the most distinctive cities in North Texas — a college town with a fiercely independent spirit, bookended by University of North Texas and Texas Woman's University, and anchored by one of the most vibrant music scenes in the state. With more than 150,000 residents and two major universities enrolling a combined 60,000+ students, Denton generates substantial international travel demand from study abroad programs, academic conferences, research partnerships, and the city's unique music and arts export culture.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            University of North Texas is home to one of the premier Jazz Studies programs in the world, and its College of Music sends student ensembles, individual performers, and faculty on international tours, competitions, and residencies. UNT's College of Business has partnerships across Europe and Asia, and the College of Visual Arts & Design sends students to art capitals from Paris to Tokyo. For UNT, international travel is not an occasional event — it's baked into the academic calendar.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Texas Woman's University has a strong healthcare and nursing program with international clinical partnerships, research exchange agreements, and global public health initiatives. TWU students traveling for international clinical rotations or public health fieldwork face the same passport timing challenges as any study abroad student — but with the added complexity of medical documentation and visa requirements for countries like South Africa, Thailand, and Ecuador.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Denton County is also one of the fastest-growing suburban counties in Texas. Cities like Flower Mound, Highland Village, Lewisville, and The Colony have attracted large numbers of technology professionals, many of whom work for the Fortune 500 corridor in Frisco and Plano and travel internationally for business. DFW Airport's proximity — the same distance as many DFW suburbs — makes Denton County a natural passport service market.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Denton's independent music scene — from the Denton Arts and Jazz Festival to the North Texas State Fair — creates another unique passport demand: touring musicians, festival organizers, and booking agents who travel to Europe, Japan, and Latin America for performances and industry events. These creative professionals often have unpredictable travel schedules and discover passport issues at the last possible moment.
          </p>
        </div>
      </div>
    </section>
  );
}

function DentonTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Denton Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {[
            { icon: "ri-flight-takeoff-line", title: "DFW International Airport", sub: "30 miles south of Denton — one of the world's busiest airports", body: "Dallas-Fort Worth International Airport is the primary international gateway for Denton residents, offering direct flights to more than 230 international destinations on American Airlines alone. The proximity means Denton travelers have exceptional global connectivity — but it also means people book international trips impulsively, then discover passport issues with less than 3 weeks until departure. We see this pattern constantly with Denton clients.", tip: "DFW tip: DFW is just 35 minutes from downtown Denton. The airport's passport acceptance facility is convenient for Denton residents who discover a passport issue in the final days before departure — but our courier service eliminates the need to go at all." },
            { icon: "ri-graduation-cap-line", title: "UNT & TWU International Programs", sub: "60,000+ students with growing study abroad enrollment", body: "Both UNT and TWU have dedicated study abroad offices with dozens of partner institutions worldwide. For first-generation college students — a significant population at both universities — applying for a passport is a new, unfamiliar process. First-time applicants who wait until the semester before travel are in a risky position if any documentation error occurs. Our document review service is specifically designed to catch these errors before submission.", tip: "Student tip: Study abroad programs often require you to have your passport in hand before the program office will complete your enrollment. Start the passport process 8–10 weeks before your program's enrollment deadline — not your departure date." },
            { icon: "ri-music-line", title: "UNT Jazz & Denton Music Scene", sub: "International performance, competitions, and touring", body: "UNT's Jazz Studies program competes internationally, and Denton's indie music scene produces bands that tour Europe and Japan. Tour dates are not flexible — a musician without a valid passport can miss an entire European tour leg. We process music industry passport applications with an understanding of the touring calendar, and our emergency tier is designed exactly for the 'my tour starts in 4 days' scenario.", tip: "Music tip: Many European visa programs for touring musicians require the passport to be valid for 6 months beyond the final date of the tour. Check the entire tour's date range, not just the first show." },
            { icon: "ri-building-2-line", title: "Denton County Tech Corridor", sub: "Flower Mound, Lewisville, and Highland Village business travel", body: "Denton County's suburban cities are home to thousands of technology, finance, and healthcare professionals who work in the DFW corporate corridor. These professionals travel internationally for client visits, partner conferences, and project deployments. The same last-minute booking pattern that affects Denton's student population also affects its professional class — a deal closes and a client visit is scheduled for the following week.", tip: "Business tip: Many corporate international trips to Europe and Asia require a passport with 6 months of validity. Check this before your manager asks you to be on a plane next Tuesday." },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                  <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{card.title}</h3>
                  <p className="text-gray-400 text-xs">{card.sub}</p>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.body}</p>
              <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
                <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Tip:</strong> {card.tip}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-pin-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">DFW Proximity = More International Travel = More Urgent Passports</h3>
            <p className="text-white/60 text-sm leading-relaxed">Denton residents book international flights more readily than those in cities far from a major hub. That convenience creates a specific pattern: last-minute bookings followed by passport discovery. Our emergency tier is designed exactly for Denton travelers who are departing through DFW in the next 1–7 days.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Get Started <i className="ri-arrow-right-line ml-1"></i></a>
        </div>
      </div>
    </section>
  );
}

function DentonDestinationsSection() {
  const destinations = [
    { name: "Europe (UK, Germany, France)", context: "UNT study abroad, business travel, Denton music touring in European venues", urgency: "Academic program dates and tour schedules are non-negotiable", icon: "ri-map-pin-2-line" },
    { name: "Japan", context: "UNT Jazz touring and competitions, technology business travel, cultural exchange programs", urgency: "Japan tour dates are set months in advance by promoters — no flexibility", icon: "ri-map-pin-2-line" },
    { name: "Mexico", context: "Denton family travel, spring break, border business, cultural heritage trips", urgency: "Spring break and holiday bookings peak in December–February", icon: "ri-map-pin-2-line" },
    { name: "Caribbean & Cancun", context: "DFW proximity creates easy last-minute resort bookings, family spring break travel", urgency: "Online deals booked 2–3 weeks out often expose expired passports", icon: "ri-map-pin-2-line" },
    { name: "Southeast Asia", context: "TWU nursing and healthcare clinical rotations, technology business travel, mission trips", urgency: "Clinical rotation dates are fixed by host institution calendars", icon: "ri-map-pin-2-line" },
    { name: "Latin America", context: "UNT Spanish-language and international programs, business in Mexico and Colombia", urgency: "Academic departure dates for language immersion programs are firm", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "60,000+", label: "Combined UNT and TWU students generating study-abroad passport demand" },
    { stat: "30 Miles", label: "Distance from Denton to DFW International Airport — one of the world's busiest" },
    { stat: "230+", label: "International destinations served by DFW Airport non-stop" },
    { stat: "24 Hours", label: "Fastest turnaround for emergency Denton passport requests" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Denton Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/frisco" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Frisco <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/fort-worth" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Fort Worth <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DentonTestimonialsSection() {
  const testimonials = [
    { name: "UNT Jazz Student, Marcus B.", location: "Denton", text: "European tour starting in 6 days, passport expired. Fast Passport Center processed my renewal in 48 hours. I made the flight. They saved the entire tour for our ensemble.", rating: 5 },
    { name: "Dr. Priya S.", location: "Flower Mound", text: "Needed to renew before a medical conference in Munich. They caught that my photo didn't meet spec — would have been rejected without their review. Got it back in 5 business days.", rating: 5 },
    { name: "First-Time Traveler, Kira N.", location: "TWU Student", text: "First time applying for a passport for my clinical rotation in South Africa. They explained every step and helped me understand the forms. Easy process and fast turnaround.", rating: 5 },
    { name: "The Henderson Family", location: "Lewisville", text: "Booked a last-minute Cancun trip and realized my daughter's passport was expired. 8 days until our flight. They got it back in 6 days. Absolute lifesavers.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Denton Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.location}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonFacilitiesSection() {
  const facilities = [
    { name: "Denton Main Post Office", address: "408 W Hickory St, Denton, TX 76201", phone: "(940) 387-3761", photo: "Yes", appointment: "Required" },
    { name: "Flower Mound Post Office", address: "3401 Wichita Ct, Flower Mound, TX 75028", phone: "(972) 724-5190", photo: "Yes", appointment: "Walk-in" },
    { name: "Lewisville Post Office", address: "875 E Vista Ridge Mall Dr, Lewisville, TX 75067", phone: "(972) 436-3301", photo: "Yes", appointment: "Required" },
    { name: "Denton County Clerk", address: "1450 E McKinney St, Denton, TX 76209", phone: "(940) 349-2012", photo: "No", appointment: "Walk-in" },
    { name: "Highland Village Post Office", address: "550 Village Pkwy, Highland Village, TX 75077", phone: "(972) 317-5424", photo: "Yes", appointment: "Walk-in" },
    { name: "Corinth Post Office", address: "3601 Teasley Ln, Corinth, TX 76210", phone: "(940) 321-2540", photo: "Yes", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Denton County Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Denton-area options.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead. DFW Airport also has an acceptance facility with extended hours for travelers.</p>
        </div>
      </div>
    </section>
  );
}

function DentonComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Denton Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Student Experience", us: "Yes, UNT & TWU programs", post: "Generic", reseller: "Generic" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function DentonMistakesSection() {
  const mistakes = [
    { title: "Booking International Flights Before Checking Passports", desc: "DFW's proximity makes it easy for Denton residents to impulsively book international flights. Always check your passport expiration before clicking 'purchase.' Many of our emergency requests come from Denton clients who booked first and checked later." },
    { title: "Underestimating First-Time Application Complexity", desc: "First-generation travelers at UNT and TWU often don't realize that first-time applications require an in-person acceptance facility visit, specific document types, and correct photo specifications. A single error can cause rejection and a multi-week delay." },
    { title: "Not Checking the 6-Month Validity Rule", desc: "Japan, the UK, and many other Denton music and academic travel destinations require 6 months of passport validity beyond your return date. If you're a UNT Jazz student going to Tokyo and your passport expires 3 months after your return, you'll be turned away." },
    { title: "Counting Business Days Incorrectly", desc: "Standard expedited processing takes 5–10 'business days,' which means weekends and holidays don't count. Denton students planning for a Wednesday departure that is 8 calendar days away may have only 5–6 business days — requiring our rush tier, not standard." },
    { title: "Not Using Document Review", desc: "Acceptance facilities in Denton can't tell you if your citizenship document is acceptable or if your photo meets spec before you submit. Rejections at acceptance facilities require starting over. Our document review prevents this." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Denton Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Denton-area acceptance facility. We find the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application via overnight courier. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Denton address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Denton Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Denton Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Denton Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line", title: "U.S. State Department", desc: "Official passport information, forms, and processing status updates." },
            { href: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html", icon: "ri-earth-line", title: "Travel Advisories", desc: "Current safety and entry requirements for every country." },
            { href: "https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program", icon: "ri-visa-line", title: "Visa Waiver Program", desc: "Check if your destination participates in the ESTA visa waiver program." },
          ].map((r, i) => (
            <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function DentonFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Denton Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are a UNT student heading abroad, a musician with a European tour, or a Denton family with a DFW departure this week, we will get your passport processed on time.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function DentonPage() {
  useEffect(() => {
    document.title = "Emergency Passport Denton TX — Expedited Service for UNT, TWU & DFW Travelers | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/denton/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Denton TX for UNT students, TWU students, musicians, and Denton County families near DFW Airport. As fast as 24 hours. A+ BBB rated registered State Dept. courier.");
    setMeta("geo.placename", "Denton, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "33.2148;-97.1331");
    setMeta("ICBM", "33.2148, -97.1331");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Denton Passport Services", item: pageUrl },
      ]},
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-denton";
    document.getElementById("structured-data-denton")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => { document.getElementById("structured-data-denton")?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <DentonHeroSection />
      <DentonStickyNav />
      <DentonProcessingSection />
      <DentonServicesSection />
      <DentonLocalAreaSection />
      <DentonTravelTimingSection />
      <DentonDestinationsSection />
      <DentonTestimonialsSection />
      <DentonFacilitiesSection />
      <DentonComparisonSection />
      <DentonMistakesSection />
      <DentonHowItWorksSection />
      <DentonFAQSection />
      <DentonResourcesSection />
      <SEOCrossLinks currentRoute="/cities/denton" state="TX" />
      <DentonFinalCTA />
    </div>
  );
}